/**
 * The shell: navigation rail, top bar, theme switch, routes (§11.1).
 *
 * The rail is the collapsed 64px form from `AuditWorkspace.dc.html`; the full 240px
 * navigation from `Main.dc.html` arrives with Phase 1's screens. Routes are added as
 * their phases land — the map is in §11.1 and is not improvised per screen.
 */

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Route, Routes, useLocation } from 'react-router-dom';

import { Avatar, CalendarGlyph, FindingGlyph, FolderGlyph, GaugeGlyph } from '@portal/ui';

import { api } from './api.js';
import { FoundationsRoute } from './routes/foundations.js';
import { PortfolioRoute } from './routes/portfolio.js';
import { ProjectNewRoute } from './routes/project-new.js';
import { ProjectOverviewRoute } from './routes/project-overview.js';
import { AuditWorkspaceRoute } from './routes/audit-workspace.js';
import { ReportsRoute } from './routes/reports.js';
import { WaiversRoute } from './routes/waivers.js';
import { devBypass, signOut } from './auth/msal.js';
import { AuditNewRoute } from './routes/audit-new.js';
import { AuditReviewRoute } from './routes/audit-review.js';
import { AuditMatrixRoute } from './routes/audit-matrix.js';
import { AuditEvidenceRoute } from './routes/audit-evidence.js';

type Theme = 'light' | 'dark' | 'system';

const THEME_KEY = 'portal.theme';

function useTheme(): [Theme, (next: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(THEME_KEY) as Theme | null) ?? 'system',
  );

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', theme);
    }
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  return [theme, setTheme];
}

function RailLink({ to, label, children }: { to: string; label: string; children: ReactNode }) {
  const { pathname } = useLocation();
  const active = to === '/' ? pathname === '/' : pathname.startsWith(to);

  return (
    <Link
      to={to}
      title={label}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      style={{
        width: 40,
        height: 34,
        borderRadius: 'var(--r-lg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: active ? 'var(--rail-tx-on)' : 'var(--rail-tx)',
        background: active ? 'var(--rail-on)' : 'transparent',
        textDecoration: 'none',
      }}
    >
      {children}
    </Link>
  );
}

export function App() {
  const [theme, setTheme] = useTheme();
  const me = useQuery({ queryKey: ['me'], queryFn: api.me });

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
      <nav
        style={{
          width: 'var(--w-rail)',
          flex: '0 0 var(--w-rail)',
          background: 'var(--rail)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
          padding: '14px 0',
          position: 'sticky',
          top: 0,
          height: '100vh',
        }}
      >
        <Link
          to="/"
          aria-label="Maintenance Readiness"
          style={{
            width: 28,
            height: 28,
            borderRadius: 7,
            background: 'var(--ac)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 10,
          }}
        >
          <svg
            viewBox="0 0 16 16"
            width="16"
            height="16"
            aria-hidden="true"
            style={{ color: '#fff' }}
          >
            <path
              d="M8 1.8l5.2 2v4.4c0 3.1-2.1 5.8-5.2 6.9-3.1-1.1-5.2-3.8-5.2-6.9V3.8z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinejoin="round"
            />
            <path
              d="M5.6 8.1l1.8 1.8 3.2-3.6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Link>

        <RailLink to="/" label="Portfolio">
          <GaugeGlyph size={15} />
        </RailLink>
        <RailLink to="/findings" label="Findings">
          <FindingGlyph size={15} />
        </RailLink>
        <RailLink to="/schedule" label="Schedule">
          <CalendarGlyph size={15} />
        </RailLink>
        <RailLink to="/foundations" label="Design foundations">
          <FolderGlyph size={15} />
        </RailLink>

        <div style={{ marginTop: 'auto' }}>
          {me.data !== undefined && (
            <Avatar initials={me.data.initials} name={me.data.displayName} size={26} />
          )}
        </div>
      </nav>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <header
          style={{
            height: 56,
            flex: '0 0 56px',
            background: 'var(--sf)',
            borderBottom: '1px solid var(--bd)',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '0 24px',
          }}
        >
          <span style={{ fontSize: 12.5, color: 'var(--mu)' }}>Actimage</span>

          {me.data?.isDevSession === true && (
            <span
              title="AUTH_DEV_BYPASS is on. The API refuses to start with this outside local and ci."
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                height: 20,
                padding: '0 6px',
                borderRadius: 'var(--r-md)',
                border: '1px solid var(--wabd)',
                background: 'var(--wab)',
                color: 'var(--wa)',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              Local sign-in bypass
            </span>
          )}

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 11.5,
                color: 'var(--mu)',
              }}
            >
              Theme
              <select
                value={theme}
                onChange={(event) => setTheme(event.target.value as Theme)}
                style={{
                  height: 26,
                  borderRadius: 'var(--r-md)',
                  border: '1px solid var(--bd)',
                  background: 'var(--sf)',
                  color: 'var(--tx)',
                  font: '500 11.5px/1 var(--font-sans)',
                  padding: '0 6px',
                }}
              >
                <option value="system">System</option>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </label>

            {me.data !== undefined && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar initials={me.data.initials} name={me.data.displayName} size={26} />
                <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{me.data.displayName}</span>
                  <span
                    style={{ fontSize: 10.5, color: 'var(--mu)' }}
                    title={me.data.orgRoleSource ?? undefined}
                  >
                    {me.data.orgRole}
                  </span>
                </div>
                {/*
                  Hidden under the bypass: there is no session to end, and a sign-out
                  button that does nothing is worse than no button.
                */}
                {!devBypass && (
                  <button
                    type="button"
                    onClick={() => {
                      void signOut();
                    }}
                    title="Sign out"
                    style={{
                      height: 26,
                      padding: '0 9px',
                      marginLeft: 4,
                      borderRadius: 'var(--r-md)',
                      border: '1px solid var(--bd)',
                      background: 'var(--sf)',
                      color: 'var(--mu)',
                      font: '500 11px/1 var(--font-sans)',
                      cursor: 'pointer',
                    }}
                  >
                    Sign out
                  </button>
                )}
              </div>
            )}
          </div>
        </header>

        <main style={{ flex: 1, minHeight: 0 }}>
          <Routes>
            <Route path="/" element={<PortfolioRoute />} />
            <Route path="/projects/new" element={<ProjectNewRoute />} />
            <Route path="/projects/:id" element={<ProjectOverviewRoute />} />
            <Route path="/projects/:id/reports" element={<ReportsRoute />} />
            <Route path="/projects/:id/waivers" element={<WaiversRoute />} />
            <Route path="/projects/:id/audits/new" element={<AuditNewRoute />} />
            <Route path="/audits/:id" element={<AuditWorkspaceRoute />} />
            <Route path="/audits/:id/review" element={<AuditReviewRoute />} />
            <Route path="/audits/:id/matrix" element={<AuditMatrixRoute />} />
            <Route path="/audits/:id/evidence" element={<AuditEvidenceRoute />} />
            <Route path="/foundations" element={<FoundationsRoute />} />
            {/* §11.1 maps every remaining artboard to its route; they land with their phases. */}
            <Route
              path="*"
              element={
                <div style={{ padding: '24px 28px', color: 'var(--mu)', fontSize: 13 }}>
                  Not built yet — see §11.1 for which phase this screen belongs to.
                </div>
              }
            />
          </Routes>
        </main>
      </div>
    </div>
  );
}
